import { getActiveFilter } from '../../../features/data-types'
import { O } from '../../../features/ui'
import { QueryOptions } from '../../../services/dynamo'
import { TableName } from '../../../tables'
import { toList } from '../../../utils/listUtils'
import { ListResponse, useLoaders } from '../../common'
import { useSystemStateHooks } from '../../system'
import { Product, ProductType } from '../state/types'

export const useProductLoaders = () => {
    const systemHooks = useSystemStateHooks()
    
    const loaders = useLoaders()

    return (companyId = systemHooks.companyId) => {
        const loadAllProducts = async (active = false) => {
            const { loadMultiple } = loaders(companyId)
            const options = active ? getActiveFilter() : undefined
            const response = await loadMultiple(TableName.PRODUCTS, options)
            return response.dataTypes as Product[]
        }

        const loadProductsBatch = async (options?: QueryOptions) => {
            const { loadMultiple } = loaders(companyId)
            return loadMultiple(TableName.PRODUCTS, options) as Promise<ListResponse<Product>>
        }

        const loadActiveProducts = async () => {
            const options = getActiveFilter()
            const response = await loadProductsBatch(options)
            return response.dataTypes
        }

        // TODO analizar los de abajo de esta linea 1 por 1 si se puede reemplazar por loadDeliveriesBatch

        const loadProduct = (
            id: string,
            dbRequest = false
        ) => {
            const { load } = loaders(companyId)
            return load(TableName.PRODUCTS, id, dbRequest) as Promise<O<Product>>
        }

        const loadProducts = (ids: string[]) => {
            const { loadByIds } = loaders(companyId)
            return loadByIds(TableName.PRODUCTS, ids) as Promise<Product[]>
        }

        const loadTypeProducts = (type: ProductType, dbRequest = false) => {
            const { loadFiltered } = loaders(companyId)
            return loadFiltered(TableName.PRODUCTS, dbRequest, { type }) as Promise<Product[]>
        }

        const loadTransformableProducts = () => {
            const { loadFiltered } = loaders(companyId)
            return loadFiltered(TableName.PRODUCTS, false, undefined, undefined, ['equivalences']) as Promise<Product[]>
        }

        const loadSupplierCodesProducts = async (supplierCodes: string[]) => {
            const { loadFilteredByList } = loaders(companyId)
            return loadFilteredByList(TableName.PRODUCTS, false, 'supplierCode', supplierCodes) as Promise<Product[]>
        }

        const loadProductsWithProp = async (
            propName: string,
            dbRequest = false
        ) => {
            const { loadFiltered } = loaders(companyId)
            return loadFiltered(TableName.PRODUCTS, dbRequest, undefined, undefined, toList(propName)) as Promise<Product[]>
        }

        const countProducts = (propName: string, propValue: string) => {
            const options = {
                valueFilters: [{
                    value: propValue,
                    prop: propName
                }],
                limit: 1
            }
            const { count2 } = loaders(companyId)
            return count2(TableName.PRODUCTS, options)
        }

        return {
            loadProductsBatch,
            loadActiveProducts,
            loadAllProducts,
            loadProduct,
            loadProducts,
            loadTypeProducts,
            loadTransformableProducts,
            loadSupplierCodesProducts,
            loadProductsWithProp,
            countProducts
        }
    }
}
